import React from 'react'
import {Card, CardContent} from '@/components/ui/card'
import PropTypes from 'prop-types'
import Heading from '../typography/heading'
import {Button} from 'hec/components/cta'
import {buttonFunctions} from 'hec/builder/blocks/cta'
import {useDetermineRouteTypeAndUrl} from 'hec/hooks/useDetermineRouteTypeAndUrl'
import {cn} from '@/lib/utils'
import {BodyCopy} from '@/components/ui/body-copy'
import modelMap from 'hec/builder/model-map'
import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'

export const CustomArticleCard = (props) => {
    const {articleRef, ctaText = 'Read More', attributes} = props
    const {className, ...restAttributes} = attributes || {}

    const shouldQueryArticle = !articleRef?.value?.data?.page?.value

    const {data: articleData, error: articleError} = useFetchBuilderReference({
        model: modelMap.learningCenterArticle,
        shouldQuery: shouldQueryArticle,
        referenceId: articleRef?.id,
        fields: 'data'
    })

    const formatArticleData = () => {
        const data = shouldQueryArticle ? articleData?.data : articleRef?.value?.data
        return {
            image: data?.image,
            page: data?.page,
            title: data?.title
        }
    }
    const {image, page, title} = formatArticleData()
    const {routeType, url} = useDetermineRouteTypeAndUrl({
        clickFunction: buttonFunctions.linkToPage,
        isInternalLink: true,
        pageReference: page?.value?.data
    })

    if (shouldQueryArticle && articleError) {
        console.error(`An error occurred while fetching article data: `, articleError)
    }

    return (
        <Card
            className={cn(
                'border-solid border-border overflow-hidden sm:h-[25rem] h-[18.75rem] mx-auto',
                className
            )}
            {...restAttributes}
            data-testid="articleCard"
        >
            <Button
                routeType={routeType}
                url={url}
                clickFunction={buttonFunctions.linkToPage}
                data-testid="articleCard-LinkWrapper"
            >
                <CardContent className="flex flex-col h-full" data-testid="articleCard-CardContent">
                    <img
                        src={image}
                        alt={title}
                        className="w-full h-2/5 object-cover rounded-t-lg"
                    />

                    <div className="lg:px-3 px-1 py-4 text-center">
                        <Heading
                            tag="h1"
                            variant="h1"
                            content={title}
                            className="lg:text-base text-sm lg:leading-7 leading-5 line-clamp-6"
                            data-testid="articleCard-Heading"
                        />
                    </div>
                    <div className="p-2 text-center mt-auto">
                        <BodyCopy
                            size="small"
                            className="!font-medium inline tracking-[.07rem] underline text-emphasis hover:text-emphasis/90"
                            data-testid="articleCard-BodyCopy"
                        >
                            {ctaText}
                        </BodyCopy>
                    </div>
                </CardContent>
            </Button>
        </Card>
    )
}

CustomArticleCard.propTypes = {
    attributes: PropTypes.object,
    articleRef: PropTypes.shape(),
    ctaText: PropTypes.string
}

export const ArticleCardDefinition = {
    component: CustomArticleCard,
    name: 'ArticleCard',
    noWrap: true,
    inputs: [
        {
            name: 'articleRef',
            type: 'reference',
            model: 'learning-center-article',
            friendlyName: 'Select Article',
            required: true
        },
        {
            name: 'ctaText',
            type: 'string',
            defaultValue: 'Read More'
        }
    ]
}
